import React, {
  useContext, useEffect, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Box, Flex } from '@chakra-ui/react';
import { SideBar, TEEAuthDataContext } from '@texas-education-exchange/edx-portal-shared';
import { useNavigate } from 'react-router-dom';
import {
  DashboardIcon,
  GroupsIcon,
  HelpIcon,
  TrainingIcon,
  SearchIcon,
  MoreHorizIcon,
  EventsIcon,
  ReportsIcon,
  AssessmentIcon,
  WarningIcon,
  NotificationIcon,
  PersonAddIcon,
} from '../icons/index';
import { isExchangeAdmin } from '../utils/helpers';
import { useConfiguration } from '../hooks/configuration.hooks';
import CustomBadge from '../components/ElementaryComponents/CustomBadge';
import routes from '../core/routes';

const scrollPaneSelector = ':scope > div > div:nth-of-type(1)';
const scrollListSelector = `${scrollPaneSelector} > div:nth-of-type(1)`;

const NavScrollArrow = ({ direction, onClick }) => (
  <Box
    as="button"
    type="button"
    aria-label={direction === 'up' ? 'Scroll navigation up' : 'Scroll navigation down'}
    position="absolute"
    left="0"
    right="0"
    top={direction === 'up' ? 0 : undefined}
    bottom={direction === 'down' ? 0 : undefined}
    zIndex="2"
    h="35px"
    display="flex"
    alignItems="center"
    justifyContent="center"
    border="none"
    cursor="pointer"
    bgGradient={direction === 'up'
      ? 'linear(to-b, blue.600 70%, transparent)'
      : 'linear(to-t, blue.600 70%, transparent)'}
    onMouseDown={(event) => event.stopPropagation()}
    onClick={onClick}
  >
    <Box
      aria-hidden="true"
      w="0"
      h="0"
      borderLeft="8px solid transparent"
      borderRight="8px solid transparent"
      borderBottom={direction === 'up' ? '9px solid white' : undefined}
      borderTop={direction === 'down' ? '9px solid white' : undefined}
    />
  </Box>
);

const LeftSidebar = ({
  navExpanded,
  setNavExpanded,
  access_sidebar,
  userData = {},
}) => {
  const iconStyle = {
    fontSize: '1.2rem',
  };
  const latest_guidelines_status = userData?.data?.latest_agreements?.community_guidelines?.status;
  const guidelines_accepted = latest_guidelines_status === 'accepted';

  const navigate = useNavigate();
  const navRef = useRef(null);
  const [scrollPane, setScrollPane] = useState(null);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);
  const { auth } = useContext(TEEAuthDataContext);
  const { tenantAccess } = useConfiguration();
  const exchangeAdmin = isExchangeAdmin(
    auth?.user?.profile?.role,
    tenantAccess?.exchangeAdminRole,
  );

  const [selectedItemId, setSelectedItemId] = useState('dashboard');

  const onChangeSelectedItemId = (id) => {
    setSelectedItemId(id);

    switch (id) {
      case 'dashboard':
        return navigate(routes.dashboard.url);
      case 'forums':
        return navigate(routes.forum.url);
      case 'trainings':
        return navigate(routes.training.url);
      case 'events':
        return navigate(routes.events.url);
      case 'search':
        return navigate(routes.search.url);
      case 'help':
        return navigate(routes.help.url);
      case 'moderation':
        return navigate(routes.reports.url);
      case 'moderationReport':
        return navigate(routes.moderationReport.url);
      case 'analytics':
        return navigate(routes.analytics.url);
      case 'infractions':
        return navigate(routes.infractions.url);
      case 'notifications-center':
        return navigate(routes.notificationCenter.url);
      case 'site-tour':
        return navigate(`/${routes.dashboard.url}?startSiteTour=true&refreshTour=${Math.floor(
          Math.random() * 1000,
        )}`);
      case 'subscriptions':
        return navigate(routes.subscriptions.url);
      case 'tenant-access':
        return navigate(routes.tenantAccess.url);
      default:
        return navigate(routes.dashboard.url);
    }
  };

  const items = [
    {
      id: 'dashboard',
      text: 'Dashboard',
      icon: (
        <DashboardIcon aria-label="Dashboard" style={{ ...iconStyle }} />
      ),
    },
    {
      id: 'forums',
      text: 'Forum',
      icon: <GroupsIcon aria-label="Forum" style={{ ...iconStyle }} />,
    },
    {
      id: 'trainings',
      text: 'Trainings',
      icon: (
        <TrainingIcon aria-label="Trainings" style={{ ...iconStyle }} />
      ),
    },
    {
      id: 'events',
      text: 'Events',
      icon: <EventsIcon aria-label="Events" style={{ ...iconStyle }} />,
    },
    {
      id: 'search',
      text: 'Search',
      icon: <SearchIcon aria-label="Search" style={{ ...iconStyle }} />,
    },
    {
      id: 'help',
      text: 'Help',
      icon: <HelpIcon aria-label="Help" style={{ ...iconStyle }} />,
    },
  ];

  let moderationSubItem = [
    { id: 'moderation', text: 'Moderation' },
  ];

  const moderationReportSubItem = [
    { id: 'moderationReport', text: 'Moderation Reports' },
  ];

  if (access_sidebar?.infraction_report || access_sidebar?.moderator_report) {
    moderationSubItem = [...moderationSubItem, ...moderationReportSubItem];
  }

  if (access_sidebar?.moderation
    || access_sidebar?.infraction_report
    || access_sidebar?.moderator_report) {
    items.push({
      // Add extras to the sidebar
      id: 'moderation',
      text: 'Moderation',
      icon: (
        <div style={{ position: 'relative' }}>
          <ReportsIcon aria-label="Reports" style={{ ...iconStyle }} />
          <CustomBadge
            itemCount={access_sidebar?.pending_moderation_report}
            aria-label={`${access_sidebar?.pending_moderation_report} new reports`}
            style={{
              top: '-6px',
              right: '-10px',
              variant: 'subtle',
              fontSize: '0.75rem',
            }}
          />
        </div>
      ),
      subItems: moderationSubItem,
    });
  }

  if (access_sidebar?.activity_report) {
    // Add analytics to the sidebar
    items.push({
      id: 'analytics',
      text: 'Analytics',
      icon: <AssessmentIcon aria-label="Analytics" style={{ ...iconStyle }} />,
    });
  }

  // Add infractions to the sidebar
  items.push({
    id: 'infractions',
    text: 'Infractions',
    icon: <WarningIcon aria-label="Infractions" style={{ ...iconStyle }} />,
  });

  if (access_sidebar?.notification) {
    // Add notification center to the sidebar
    items.push({
      id: 'notifications-center',
      text: 'Notification Center',
      icon: <NotificationIcon aria-label="Notifications Center" style={{ ...iconStyle }} />,
    });
  }

  if (exchangeAdmin) {
    items.push({
      id: 'tenant-access',
      text: 'Tenant Access',
      icon: (
        <div style={{ position: 'relative' }}>
          <PersonAddIcon
            aria-label="Tenant Access, Exchange Administrators only"
            style={{ ...iconStyle }}
          />
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: 7,
              height: 7,
              borderRadius: '50%',
              background: '#f26b1d',
            }}
          />
        </div>
      ),
    });
  }

  const extraSubItems = [
    { id: 'subscriptions', text: 'Subscriptions' },
  ];

  if (guidelines_accepted) {
    // Add site tour to the sidebar
    extraSubItems.unshift({ id: 'site-tour', text: 'Site Tour' });
  }

  const scrollNav = (direction) => (event) => {
    event.stopPropagation();
    const list = navRef.current?.querySelector(scrollListSelector);
    if (!list) {
      return;
    }
    const hidden = direction > 0
      ? list.scrollHeight - list.clientHeight - list.scrollTop
      : list.scrollTop;
    // Move by one visible screen. A shorter remainder goes the rest of the way.
    const distance = Math.min(list.clientHeight, hidden);
    list.scrollBy({ top: direction * distance, behavior: 'smooth' });
  };

  useEffect(() => {
    const root = navRef.current;
    const list = root?.querySelector(scrollListSelector);
    const pane = root?.querySelector(scrollPaneSelector) ?? null;
    setScrollPane(pane);
    if (!list) {
      setCanScrollUp(false);
      setCanScrollDown(false);
      return undefined;
    }

    const update = () => {
      setCanScrollUp(list.scrollTop > 1);
      setCanScrollDown(
        list.scrollTop + list.clientHeight < list.scrollHeight - 1,
      );
    };
    update();
    list.addEventListener('scroll', update, { passive: true });
    const resizeObserver = new ResizeObserver(update);
    // Submenus grow inside the list, so the list box itself does not resize.
    const watchSizes = () => {
      resizeObserver.disconnect();
      resizeObserver.observe(list);
      list.querySelectorAll('*').forEach((node) => resizeObserver.observe(node));
    };
    watchSizes();
    const mutationObserver = new MutationObserver(() => {
      watchSizes();
      update();
    });
    mutationObserver.observe(list, { childList: true, subtree: true });
    let settleFrame;
    let settleTimer;
    const settleAfterToggle = () => {
      update();
      window.cancelAnimationFrame(settleFrame);
      window.clearTimeout(settleTimer);
      settleFrame = window.requestAnimationFrame(update);
      settleTimer = window.setTimeout(update, 250);
    };
    list.addEventListener('click', settleAfterToggle);
    window.addEventListener('resize', update);
    return () => {
      list.removeEventListener('scroll', update);
      list.removeEventListener('click', settleAfterToggle);
      window.cancelAnimationFrame(settleFrame);
      window.clearTimeout(settleTimer);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [access_sidebar, exchangeAdmin, guidelines_accepted]);

  items.push({
    // Add extras to the sidebar
    id: 'extras',
    text: 'Extras',
    icon: (
      <MoreHorizIcon aria-label="Extra Options" style={{ fontSize: '0.8rem' }} />
    ),
    subItems: extraSubItems,
  });

  return (
    <Flex
      ref={navRef}
      alignItems="center"
      id="LeftNavBar"
      bg="blue.600"
      flexDirection="column"
      height="100%"
      minHeight="84vh"
      cursor="pointer"
      onClick={() => setNavExpanded(!navExpanded)}
    >
      {access_sidebar && (
      <SideBar
        data-testid="sidebar"
        show
        onClickItem={onChangeSelectedItemId}
        ariaCurrentType="page"
        items={items}
        selectedItemId={selectedItemId}
        borderColor="hsla(0, 0%, 100%, 0.1)"
        hoverColor="#0E2D88"
        activeColor="#16369C"
        activeSubItemColor="#16369C"
        subItemHoverColor="#0E2D88"
        style={{ gap: '10px' }}
      />
      )}
      {scrollPane && createPortal(
        <>
          {canScrollUp && <NavScrollArrow direction="up" onClick={scrollNav(-1)} />}
          {canScrollDown && <NavScrollArrow direction="down" onClick={scrollNav(1)} />}
        </>,
        scrollPane,
      )}
    </Flex>

  );
};

export default LeftSidebar;
